import React, { useState } from "react";
import {
  Package,
  Truck,
  MapPin,
  ChevronDown,
  ChevronRight,
  Loader,
  Trash2,
  Printer,
} from "lucide-react";
import {
  Group,
  DeliveryOrder,
  AssignedCart,
} from "../../../../redux/deliveryRouteOptimizer";
import "./index.css";
import useGroupOrderPrint from "../../../../hooks/useGroupOrderPrint";
import Swal from "sweetalert2";

interface ActualGroupDetailsViewProps {
  groups: Group[];
  deliveries: DeliveryOrder[];
  vehicles: AssignedCart[];
  selectedGroupsForMap?: string[];
  toggleGroupSelectionForMap?: (groupId: string) => void;
  showOnlySelectedGroups?: boolean;
  setShowOnlySelectedGroups?: (show: boolean) => void;
  onUpdateGroupOrderStatus?: (groupId: string, newStatus: string) => void;
  isUpdatingGroupStatus?: boolean;
  updatingGroupId?: string | null;
  onRemoveOrderFromGroup?: (groupId: string, orderId: string) => void;
  isRemovingOrder?: boolean;
  removingOrderId?: string | null;
}

const getOptimizedRouteOrder = (group: Group) => {
  if (group.deliveryRoute?.route && group.deliveryRoute.route.length > 0) {
    return group.deliveryRoute.route.map((routePoint) => routePoint.orderId);
  }
  return group.deliveryIds || [];
};

const getDisplayStatus = (status: string) => {
  const mapping: Record<string, string> = {
    Collecting: "Collecting",
    ArrivedAtCart: "Dispatched",
    Completed: "Completed",
  };
  return mapping[status] || status;
};

const ActualGroupDetailsView: React.FC<ActualGroupDetailsViewProps> = ({
  groups,
  deliveries,
  vehicles,
  selectedGroupsForMap = [],
  toggleGroupSelectionForMap = () => {},
  showOnlySelectedGroups = false,
  setShowOnlySelectedGroups = () => {},
  onUpdateGroupOrderStatus = () => {},
  isUpdatingGroupStatus = false,
  updatingGroupId = null,
  onRemoveOrderFromGroup = () => {},
  isRemovingOrder = false,
  removingOrderId = null,
}) => {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {},
  );
  const [orderPrintingStates, setOrderPrintingStates] = useState<
    Record<string, boolean>
  >({});

  const getVehicleNameForGroup = (group: Group) => {
    if (!group.cart) return "No cart assigned";

    // Check if we have deliveryCartId
    const deliveryCartId = (group.cart as any)?.deliveryCartId;
    const displayName = group.cart?.displayName;

    if (displayName) {
      return displayName;
    }

    // Fallback: find vehicle by ID
    if (deliveryCartId) {
      const vehicle = vehicles.find((v) => v.id === deliveryCartId);
      return vehicle?.name || "Unknown Cart";
    }

    // Last resort
    return group.cart?.cartId || "Unknown Cart";
  };
  const toggleGroupExpansion = (groupId: string) => {
    setExpandedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  const formatDistance = (distanceInKm: number): string => {
    return `${distanceInKm.toFixed(2)} km`;
  };

  const { printGroupOrdersByGroup, printOrder, isPrinting } =
    useGroupOrderPrint({
      groups,
      deliveries,
    });

  const handlePrint = async (
    groupId: string,
    groupName: string,
    copyType: "rider" | "customer",
  ) => {
    try {
      await printGroupOrdersByGroup({
        groupId,
        groupName,
        copyType,
      });
    } catch (error) {
      console.error("Printing error:", error);
    }
  };

  const handleOrderPrint = async (
    orderId: string,
    orderReference: string,
    copyType: "rider" | "customer",
  ) => {
    setOrderPrintingStates((prev) => ({ ...prev, [orderId]: true }));

    try {
      await printOrder({
        orderId,
        orderReference,
        copyType,
      });
    } catch (error) {
      console.error("Order printing error:", error);
    } finally {
      setOrderPrintingStates((prev) => ({ ...prev, [orderId]: false }));
    }
  };

  const handleGroupSelection = (groupId: string) => {
    toggleGroupSelectionForMap(groupId);
    if (!showOnlySelectedGroups && selectedGroupsForMap.length === 0) {
      setShowOnlySelectedGroups(true);
    }
  };

  const handleStatusChange = (groupId: string, displayStatus: string) => {
    if (displayStatus) {
      // Map display status to payload status
      const statusMapping: Record<string, string> = {
        Collecting: "Collecting",
        Dispatched: "Dispatched",
        Completed: "Completed",
      };

      const payloadStatus = statusMapping[displayStatus] || displayStatus;
      onUpdateGroupOrderStatus(groupId, payloadStatus);
    }
  };

  const handleCancelGroup = (groupId: string, groupName: string) => {
    Swal.fire({
      title: "Cancel Group Order?",
      html: `
        <div style="text-align: center;">
          <p>Are you sure you want to cancel the group order <strong>"${groupName}"</strong>?</p>
          <p style="color: #ef4444; font-weight: 500; margin-top: 8px;">
            This action cannot be undone.
          </p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, cancel it!",
      cancelButtonText: "No, keep it",
      reverseButtons: true,
      customClass: {
        popup: "group-cancel-confirmation",
        confirmButton: "group-cancel-confirm-btn",
        cancelButton: "group-cancel-cancel-btn",
      },
      buttonsStyling: true,
      showLoaderOnConfirm: true,
      preConfirm: async () => {
        try {
          await onUpdateGroupOrderStatus(groupId, "Cancelled");
          return true;
        } catch (error) {
          Swal.showValidationMessage(`Cancellation failed: ${error}`);
          return false;
        }
      },
    });
  };

  const handleRemoveOrder = (
    groupId: string,
    orderId: string,
    orderReference: string,
  ) => {
    Swal.fire({
      title: "Remove Order from Group?",
      html: `
        <div style="text-align: center;">
          <p>Remove order <strong>${orderReference}</strong> from this group?</p>
          <p style="color: #ef4444; font-weight: 500; margin-top: 8px;">
            It will go back to the unassigned orders list.
          </p>
        </div>
      `,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, remove it!",
      cancelButtonText: "Cancel",
      reverseButtons: true,
      customClass: {
        popup: "order-remove-confirmation",
        confirmButton: "order-remove-confirm-btn",
        cancelButton: "order-remove-cancel-btn",
      },
      buttonsStyling: true,
    }).then((result) => {
      if (result.isConfirmed) {
        onRemoveOrderFromGroup(groupId, orderId);
      }
    });
  };

  const renderGroupCard = (group: Group) => {
    const optimizedOrderIds = getOptimizedRouteOrder(group);
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const assignedVehicle = group.cart
      ? vehicles.find((v) => v.id === group.cart?.cartId)
      : null;
    const hasDeliveryRoute =
      group.deliveryRoute && group.deliveryRoute.distanceInKm > 0;
    const isGroupExpanded = expandedGroups[group.id];
    const isSelectedForMap = selectedGroupsForMap.includes(group.id);
    const isUpdating = updatingGroupId === group.id && isUpdatingGroupStatus;

    return (
      <div key={group.id} className="actual-group-card">
        {/* Group Header - UPDATED LAYOUT */}
        <div
          className="actual-group-card__header"
          onClick={() => toggleGroupExpansion(group.id)}
          style={{ cursor: "pointer" }}
        >
          <div className="actual-group-card__top-row">
            {/* Left side: Checkbox, Expand Icon, Group Name, Status Dropdown */}
            <div className="actual-group-card__left-section">
              {/* Checkbox for group selection */}
              <div
                className="actual-group-card__checkbox-container"
                onClick={(e) => e.stopPropagation()}
              >
                <input
                  type="checkbox"
                  checked={isSelectedForMap}
                  onChange={() => handleGroupSelection(group.id)}
                  id={`group-checkbox-${group.id}`}
                />
              </div>

              {/* Expand/Collapse Icon */}
              <div className="actual-group-card__expand-icon">
                {isGroupExpanded ? (
                  <ChevronDown size={18} />
                ) : (
                  <ChevronRight size={18} />
                )}
              </div>

              <div className="actual-group-card__title-section">
                <h3 className="actual-group-card__name">{group.name}</h3>

                {/* Status Update Dropdown */}
                {group.finalizedGroupOrderId && (
                  <div
                    className="actual-group-card__status-controls"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <div className="actual-group-card__status-select-container">
                      <select
                        value={getDisplayStatus(
                          group.finalizedGroupOrderStatus || "",
                        )}
                        onChange={(e) =>
                          handleStatusChange(group.id, e.target.value)
                        }
                        onClick={(e) => e.stopPropagation()}
                        disabled={isUpdating}
                        className={`actual-group-card__status-select ${
                          isUpdating
                            ? "actual-group-card__status-select--disabled"
                            : ""
                        }`}
                      >
                        <option value="Collecting">Collecting</option>
                        <option value="Dispatched">Dispatched</option>
                        <option value="Completed">Completed</option>
                      </select>
                      {isUpdating && (
                        <div className="actual-group-card__status-overlay">
                          <Loader size={14} className="animate-spin" />
                          <span>Updating...</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right side: Delete Button */}
            <div
              className="actual-group-card__right-section"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Cancel Button - Only show for finalized groups */}
              {group.finalizedGroupOrderId && (
                <button
                  onClick={() => handleCancelGroup(group.id, group.name)}
                  disabled={isUpdating}
                  className="actual-group-card__cancel-button"
                  title="Cancel Group Order"
                >
                  <Trash2 size={18} />
                </button>
              )}
            </div>
          </div>

          {/* Group Summary Information - Second Row */}
          <div className="actual-group-card__summary-row">
            {hasDeliveryRoute && (
              <div className="actual-group-card__route-info">
                <MapPin size={14} />
                <span className="actual-group-card__distance">
                  Distance: {formatDistance(group.deliveryRoute!.distanceInKm)}
                </span>
                <div className="actual-group-card__vehicle-info">
                  <Truck size={14} />
                  <span className="actual-group-card__vehicle-name">
                    {getVehicleNameForGroup(group)}
                  </span>
                </div>
              </div>
            )}

            {/* Assigned Vehicle */}
          </div>

          {/* Print Actions Section - Third Row */}
          <div
            className="actual-group-card__print-actions"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Print buttons for the group */}
            <div className="actual-group-card__print-buttons">
              <button
                onClick={() => handlePrint(group.id, group.name, "rider")}
                disabled={isPrinting}
                className="print-button print-button--rider"
                title="Print Rider Copy"
              >
                <Printer size={18} />
                {isPrinting ? "Printing..." : "Rider Copy"}
              </button>
              <button
                onClick={() => handlePrint(group.id, group.name, "customer")}
                disabled={isPrinting}
                className="print-button print-button--customer"
                title="Print Customer Copy"
              >
                <Printer size={18} />
                {isPrinting ? "Printing..." : "Customer Copy"}
              </button>
            </div>
          </div>
        </div>

        {/* Group Deliveries - Collapsible Content */}
        {isGroupExpanded && (
          <div className="actual-group-card__deliveries">
            {optimizedOrderIds.length === 0 ? (
              <div className="actual-group-card__empty-deliveries">
                <Package size={32} />
                <p>No deliveries in this group</p>
              </div>
            ) : (
              <div className="actual-group-card__delivery-list">
                {optimizedOrderIds.map((deliveryId, index) => {
                  const delivery = deliveries.find((d) => d.id === deliveryId);
                  if (!delivery) return null;

                  const isOrderPrinting =
                    orderPrintingStates[delivery.id] || false;

                  return (
                    <div
                      key={deliveryId}
                      className="actual-delivery-item"
                      style={{ borderLeftColor: group.color }}
                    >
                      <div className="actual-delivery-item__content">
                        <span className="actual-delivery-item__stop-number">
                          {index + 1}
                        </span>
                        <div className="actual-delivery-item__details">
                          <div className="actual-delivery-item__header">
                            <div className="actual-delivery-item__order-ref-container">
                              <div className="actual-delivery-item__order-ref">
                                {delivery.orderReference}
                              </div>
                              {/* Order-wise print buttons */}
                              <div className="actual-delivery-item__order-print-buttons">
                                <button
                                  onClick={() =>
                                    handleOrderPrint(
                                      delivery.id,
                                      delivery.orderReference,
                                      "customer",
                                    )
                                  }
                                  disabled={isOrderPrinting || isPrinting}
                                  className="order-print-button order-print-button--customer"
                                  title="Print Customer Copy for this order"
                                >
                                  <Printer size={16} />
                                  {isOrderPrinting ? "..." : "Customer"}
                                </button>
                                <button
                                  onClick={() =>
                                    handleRemoveOrder(group.id, delivery.id, delivery.orderReference)
                                  }
                                  disabled={isRemovingOrder && removingOrderId === delivery.id}
                                  className="order-print-button order-print-button--remove"
                                  title="Remove order from group"
                                >
                                  {isRemovingOrder && removingOrderId === delivery.id ? (
                                    <Loader size={16} className="animate-spin" />
                                  ) : (
                                    <Trash2 size={16} />
                                  )}
                                </button>
                              </div>
                            </div>
                          </div>
                          <div className="actual-delivery-item__customer-info">
                            <div className="actual-delivery-item__customer">
                              <strong>Customer:</strong>{" "}
                              {delivery.customer?.displayName ||
                                delivery.organization?.displayName ||
                                "No Customer"}
                            </div>
                            <div className="actual-delivery-item__address-line">
                              <strong>Address:</strong>{" "}
                              {delivery.deliveryDetails.address}
                            </div>
                            {delivery.phoneNumber && (
                              <div className="actual-delivery-item__phone">
                                <strong>Phone:</strong> {delivery.phoneNumber}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="actual-group-details-view">
      {groups.length === 0 ? (
        <div className="actual-group-details-view__empty-state">
          <Package
            size={48}
            className="actual-group-details-view__empty-icon"
          />
          <h3 className="actual-group-details-view__empty-title">
            No Actual Group Orders
          </h3>
          <p className="actual-group-details-view__empty-description">
            Create actual group orders in Step 2 to see them here
          </p>
        </div>
      ) : (
        <div className="actual-group-details-view__content">
          {groups.map((group) => renderGroupCard(group))}
        </div>
      )}
    </div>
  );
};

export default ActualGroupDetailsView;
